import React, { useState, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Button from "../../../components/button/Button";
import styles from "./InvoiceSchemaDetailScreen.module.scss";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";
import moment from "moment";

const InvoiceSchemaDetailScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get return URL from location state or use default
  const returnUrl = location.state?.returnUrl || "/invoice-schemas";

  useEffect(() => {
    loadSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSchema = async () => {
    setLoading(true);
    try {
      const response = await billingInvoicesService.getInvoiceSchema(id);
      // Handle different response structures
      const schemaData = response.data?.data || response.data || response;
      
      if (schemaData && schemaData.id) {
        setSchema(schemaData);
      } else {
        toast.error("Šema fakture nije pronađena");
        history.push("/invoice-schemas");
      }
    } catch (error) {
      console.error("Error loading invoice schema:", error);
      if (error.response?.status === 404) {
        toast.error("Šema fakture nije pronađena");
      } else {
        toast.error("Greška pri učitavanju šeme fakture");
      }
      history.push("/invoice-schemas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className={styles.container}>
          <p>Učitavanje...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!schema) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{schema.name}</h1>
          <div className={styles.headerActions}>
            <Button
              name="Nova faktura"
              onClick={() => {
                history.push({
                  pathname: "/billing-invoices/new",
                  state: {
                    prefilledData: {
                      invoiceSchemaId: schema.id,
                      billingCompanyId: schema.billingCompanyId,
                      billingCompany: schema.billingCompany, // Pass full object
                      companyId: schema.companyId,
                      company: schema.company, // Pass full object
                      billingPeriodType: schema.defaultBillingPeriodType || "monthly",
                    },
                  },
                });
              }}
            />
            <Button
              name="Nazad"
              onClick={() => history.push(returnUrl)}
            />
            <Button
              name="Izmeni"
              onClick={() => history.push(`/invoice-schemas/${id}/edit`, { returnUrl })}
            />
          </div>
        </div>

        <div className={styles.content}>
          {/* Basic Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Osnovne informacije</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Naziv:</label>
                <p>{schema.name}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Opis:</label>
                <p>{schema.description || schema.notes || "-"}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Fakturiska kompanija:</label>
                <p>{schema.billingCompany?.name || schema.billingCompanyId || "-"}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Kompanija:</label>
                <p>{schema.company?.name || schema.companyId || "-"}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Podrazumevani tip perioda:</label>
                <p>
                  {schema.defaultBillingPeriodType === "monthly" && "Mesečno"}
                  {schema.defaultBillingPeriodType === "quarterly" && "Kvartalno"}
                  {schema.defaultBillingPeriodType === "semi-annual" && "Polugodišnje"}
                  {schema.defaultBillingPeriodType === "annual" && "Godišnje"}
                  {!schema.defaultBillingPeriodType && "-"}
                </p>
              </div>
              <div className={styles.infoItem}>
                <label>Popust količina:</label>
                <p>{schema.discountQuantity || "0"}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Strategija numerisanja:</label>
                <p>
                  {schema.numberingStrategy === 1 && "1 - Automatsko numerisanje"}
                  {schema.numberingStrategy === 2 && "2 - Ručno numerisanje"}
                  {schema.numberingStrategy === 3 && "3 - Prefiks + automatsko"}
                  {schema.numberingStrategy === 4 && "4 - Šifra objekta"}
                  {!schema.numberingStrategy && "-"}
                </p>
              </div>
              {schema.invoicePrefixId && (
                <div className={styles.infoItem}>
                  <label>Prefiks fakture:</label>
                  <p>{schema.invoicePrefixId}</p>
                </div>
              )}
              {schema.effectiveFrom && (
                <div className={styles.infoItem}>
                  <label>Važi od:</label>
                  <p>{moment(schema.effectiveFrom).format("DD.MM.YYYY HH:mm")}</p>
                </div>
              )}
              {schema.effectiveTo && (
                <div className={styles.infoItem}>
                  <label>Važi do:</label>
                  <p>{moment(schema.effectiveTo).format("DD.MM.YYYY HH:mm")}</p>
                </div>
              )}
              <div className={styles.infoItem}>
                <label>Aktivna:</label>
                <p>
                  <span className={schema.isActive ? styles.activeBadge : styles.inactiveBadge}>
                    {schema.isActive ? "Da" : "Ne"}
                  </span>
                </p>
              </div>
              <div className={styles.infoItem}>
                <label>Kreirano:</label>
                <p>{schema.createdAt ? moment(schema.createdAt).format("DD.MM.YYYY HH:mm") : "-"}</p>
              </div>
            </div>
          </div>

          {/* Billing Company Information */}
          {schema.billingCompany && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Na koga ide faktura</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>ID:</label>
                  <p>{schema.billingCompany.id || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Naziv kompanije:</label>
                  <p>{schema.billingCompany.name || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>PIB:</label>
                  <p>{schema.billingCompany.taxId || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Adresa:</label>
                  <p>{schema.billingCompany.address || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Ime kontakt osobe:</label>
                  <p>{schema.billingCompany.contactFirstName || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Prezime kontakt osobe:</label>
                  <p>{schema.billingCompany.contactLastName || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Kontakt osoba:</label>
                  <p>
                    {[schema.billingCompany.contactFirstName, schema.billingCompany.contactLastName]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>Email:</label>
                  <p>
                    {schema.billingCompany.contactEmail ? (
                      <a href={`mailto:${schema.billingCompany.contactEmail}`} className={styles.link}>
                        {schema.billingCompany.contactEmail}
                      </a>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>Telefon:</label>
                  <p>
                    {schema.billingCompany.contactPhone ? (
                      <a href={`tel:${schema.billingCompany.contactPhone}`} className={styles.link}>
                        {schema.billingCompany.contactPhone}
                      </a>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>Kreirano:</label>
                  <p>
                    {schema.billingCompany.createdAt
                      ? moment(schema.billingCompany.createdAt).format("DD.MM.YYYY HH:mm")
                      : "-"}
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>Ažurirano:</label>
                  <p>
                    {schema.billingCompany.updatedAt
                      ? moment(schema.billingCompany.updatedAt).format("DD.MM.YYYY HH:mm")
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Object Code Information */}
          {schema.ObjectCode && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Šifra objekta</h2>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>ID:</label>
                  <p>{schema.ObjectCode.id || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Šifra:</label>
                  <p>{schema.ObjectCode.code || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Naziv:</label>
                  <p>{schema.ObjectCode.name || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Opis:</label>
                  <p>{schema.ObjectCode.description || "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Početni redni broj:</label>
                  <p>{schema.ObjectCode.initialOrdinalNumber !== undefined ? schema.ObjectCode.initialOrdinalNumber : "-"}</p>
                </div>
                <div className={styles.infoItem}>
                  <label>Aktivna:</label>
                  <p>
                    <span className={schema.ObjectCode.isActive !== false ? styles.activeBadge : styles.inactiveBadge}>
                      {schema.ObjectCode.isActive !== false ? "Da" : "Ne"}
                    </span>
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>Kreirano:</label>
                  <p>
                    {schema.ObjectCode.createdAt
                      ? moment(schema.ObjectCode.createdAt).format("DD.MM.YYYY HH:mm")
                      : "-"}
                  </p>
                </div>
                <div className={styles.infoItem}>
                  <label>Ažurirano:</label>
                  <p>
                    {schema.ObjectCode.updatedAt
                      ? moment(schema.ObjectCode.updatedAt).format("DD.MM.YYYY HH:mm")
                      : "-"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Schema Items */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Stavke šeme</h2>
            {schema.invoiceSchemaItems && schema.invoiceSchemaItems.length > 0 ? (
              <div className={styles.table}>
                <div className={styles.tableHeader}>
                  <div className={styles.tableCell}>Naziv</div>
                  <div className={styles.tableCell}>Opis</div>
                  <div className={styles.tableCell}>Cena (bez PDV)</div>
                  <div className={styles.tableCell}>PDV %</div>
                  <div className={styles.tableCell}>Cena (sa PDV)</div>
                  <div className={styles.tableCell}>Količina</div>
                  <div className={styles.tableCell}>Popust kol.</div>
                  <div className={styles.tableCell}>Popust %</div>
                  <div className={styles.tableCell}>Aktivna</div>
                </div>
                {schema.invoiceSchemaItems.map((item, index) => {
                  const unitPrice = typeof item.unitPrice === "number" 
                    ? item.unitPrice 
                    : (parseFloat(item.unitPrice) || 0);
                  const vatRate = typeof item.vatRate === "number"
                    ? item.vatRate
                    : (parseFloat(item.vatRate) || 20);
                  const unitPriceWithVat = item.unitPriceWithVat 
                    ? (typeof item.unitPriceWithVat === "number" ? item.unitPriceWithVat : parseFloat(item.unitPriceWithVat))
                    : unitPrice * (1 + vatRate / 100);
                  
                  return (
                    <div key={index} className={styles.tableRow}>
                      <div className={styles.tableCell}>{item.name}</div>
                      <div className={styles.tableCell}>{item.description || "-"}</div>
                      <div className={styles.tableCell}>{unitPrice.toFixed(2)} RSD</div>
                      <div className={styles.tableCell}>{vatRate.toFixed(0)}%</div>
                      <div className={styles.tableCell}>{unitPriceWithVat.toFixed(2)} RSD</div>
                      <div className={styles.tableCell}>{item.quantity !== null && item.quantity !== undefined ? item.quantity : "Auto"}</div>
                      <div className={styles.tableCell}>{item.discountQuantity || "-"}</div>
                      <div className={styles.tableCell}>{item.discountPercent ? `${item.discountPercent}%` : "-"}</div>
                      <div className={styles.tableCell}>
                        <span className={item.isActive !== false ? styles.activeBadge : styles.inactiveBadge}>
                          {item.isActive !== false ? "Da" : "Ne"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={styles.noData}>Nema stavki u ovoj šemi</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InvoiceSchemaDetailScreen;

